/**
 * Group fetching/read operations
 */
import type {Getter} from 'jotai';
import {store} from '@/store';
import {userAtom} from '@/store/user';
import {logger} from '@/lib/logger';
import {
    groupsAtom,
    groupsSearchParamsAtom,
    selectedGroupAtom,
    selectedGroupDataAtom,
} from '@/store/groups';
import type {Group, GroupData, GroupsState} from '@/types/groups';
import {buildMbApiUrl, fetchWithTimeout} from '@/util/request-utils';
import {ITEMS_PER_PAGE, S3_BASE_URL} from './constants';
import {buildGroupsFilterParams} from './query-params';

const log = logger.tag('GroupsFetch');

export async function getGroups() {
    const params = store.get(groupsSearchParamsAtom);
    const user = store.get(userAtom);

    if (!user?.auth_token) {
        store.set(groupsAtom, (prev: GroupsState) => ({
            ...prev,
            loading: false,
            error: 'User not authenticated',
        }));
        return;
    }

    store.set(groupsAtom, (prev: GroupsState) => ({
        ...prev,
        loading: true,
        error: null,
    }));

    // Build query params (excluding tokens - those go through buildMbApiUrl)
    const queryParams: Record<string, string | number> = {
        per_page: ITEMS_PER_PAGE,
        page: params.page,
        ...buildGroupsFilterParams(params),
    };

    // Use buildMbApiUrl for proper token handling (no URL encoding on MB tokens)
    const url = buildMbApiUrl(
        '/product_groups/list_admin',
        {auth_token: user.auth_token},
        queryParams
    );

    try {
        const response = await fetchWithTimeout(url);
        if (!response.ok) {
            throw new Error(`Failed to fetch groups: ${response.status}`);
        }
        const data: {results: Group[]; total: number} = await response.json();
        const {results, total} = data;

        store.set(groupsAtom, (prev: GroupsState) => {
            if (params.page === 1) {
                return {
                    items: results,
                    loading: false,
                    error: null,
                    total,
                    initialized: true,
                };
            }
            // Deduplicate when appending - prevents duplicate keys from race conditions
            const existingIds = new Set(prev.items.map((g) => g.id));
            const uniqueResults = results.filter((g) => !existingIds.has(g.id));
            return {
                items: [...prev.items, ...uniqueResults],
                loading: false,
                error: null,
                total,
                initialized: true,
            };
        });
    } catch (error) {
        const errorMessage =
            error instanceof Error ? error.message : 'Failed to fetch groups';

        store.set(groupsAtom, (prev: GroupsState) => ({
            ...prev,
            loading: false,
            error: errorMessage,
        }));
    }
}

export async function getGroupById(id: number) {
    const user = store.get(userAtom);

    if (!user?.auth_token) {
        store.set(selectedGroupAtom, null);
        return null;
    }

    try {
        const url = buildMbApiUrl(`/product_groups/${id}.json`, {
            auth_token: user.auth_token,
        });
        const response = await fetchWithTimeout(url);

        if (!response.ok) {
            throw new Error(`Failed to fetch group: ${response.status}`);
        }

        const group: Group = await response.json();
        store.set(selectedGroupAtom, group);

        return group;
    } catch {
        store.set(selectedGroupAtom, null);
        return null;
    }
}

/**
 * Fetch multiple groups by IDs in parallel
 * Returns a Map keyed by group ID (as string)
 */
export async function getGroupsByIds(
    ids: (string | number)[]
): Promise<Map<string, Group>> {
    if (ids.length === 0) {
        return new Map();
    }

    try {
        // Fetch all groups in parallel
        const groupPromises = ids.map(async (id) => {
            try {
                const group = await getGroupById(Number(id));
                return {id: String(id), group};
            } catch (error) {
                log.error('Failed to fetch group', error, {id: String(id)});
                return {id: String(id), group: null};
            }
        });

        const results = await Promise.all(groupPromises);

        // Create a Map from the results
        const groupMap = new Map<string, Group>();
        results.forEach(({id, group}) => {
            if (group) {
                groupMap.set(id, group);
            }
        });

        return groupMap;
    } catch (error) {
        log.error('Failed to fetch groups', error);
        return new Map();
    }
}

export async function getGroupDataById(
    get: Getter,
    set: (atom: typeof selectedGroupDataAtom, value: GroupData | null) => void,
    id: number
) {
    try {
        const url = `${S3_BASE_URL}/${id}/data.json`;
        const response = await fetchWithTimeout(url);

        if (!response.ok) {
            log.warn('Failed to fetch group data', {
                id,
                status: response.status,
            });
            set(selectedGroupDataAtom, null);
            return null;
        }

        const data = await response.json();
        set(selectedGroupDataAtom, data);

        return data;
    } catch {
        set(selectedGroupDataAtom, null);
        return null;
    }
}

export async function getProductGroup(
    productGroupId: string,
    authToken: string
): Promise<Group> {
    const url = buildMbApiUrl(`/product_groups/${productGroupId}.json`, {
        auth_token: authToken,
    });
    const response = await fetchWithTimeout(url, {
        headers: {'Content-Type': 'application/json'},
    });

    if (!response.ok) {
        throw new Error(
            `Failed to fetch product group ${productGroupId}: ${response.status}`
        );
    }

    return response.json();
}
